
import { useEffect, useState } from "react";
import { Button, Col, Row, Nav, Tab } from "react-bootstrap";
import Accordion from 'react-bootstrap/Accordion';
import axios from 'axios';
import { withAuth } from '../../api/withAuth';
import { ReportedSoilTable } from "../admin/ReportedSoilTable";
import { ReportedSoilANumberTable } from "../admin/ReportedSoilANumberTable";
import { ReportedDrillHoleTable } from "../admin/ReportedDrillHoleTable";
import { ReportedDrillHoleANumberTable } from "../admin/ReportedDrillHoleANumberTable";
import { SoilModifierTable } from "../admin/SoilModifierTable";
import { DrillHoleModifierTable } from "../admin/DrillHoleModifierTable";
import { AddMultiplerModal } from "../admin/AddMultiplierModal";
import { AddDrillHoleModifierModal } from "../admin/AddDrillHoleModifierModal";
import ModifierProgressDisplay from "../admin/ModifierProgressDisplay";
import SoilCorrectionImport from "../admin/SoilCorrectionImport";
import CorrectedANumbersTable from "../admin/CorrectedANumbersTable";
import ANumberCorrectionsModal from "../admin/ANumberCorrectionsModal";

const GoldFinderApi = withAuth(axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_API_URL
    //baseURL: "http://localhost:3000"
}))

function AdminPage() {
    // The modifier progress stream is an EventSource, which cannot send the
    // token; the API takes its admin session cookie for that GET instead.
    useEffect(() => {
        GoldFinderApi.post('/session').catch(() => { /* not an admin, or API without sessions yet */ });
    }, []);

    const [reportedSoils, setReportedSoils] = useState({ reportedSamples: [], reportedAnumbers: [] });
    const [reportedDrillHoles, setReportedDrillHoles] = useState({ reportedSamples: [], reportedAnumbers: [] });
    const [soilModifiers, setSoilModifiers] = useState([])
    const [drillHoleModifiers, setDrillHoleModifiers] = useState([])
    const [modalShow, setModalShow] = useState(false);
    const [drillHoleModalShow, setDrillHoleModalShow] = useState(false);
    const [applyingModifiers, setApplyingModifiers] = useState(false);
    const [jobStatus, setJobStatus] = useState(null);
    const [jobId, setJobId] = useState(null);
    const [applyingDrillHoleModifiers, setApplyingDrillHoleModifiers] = useState(false);
    const [drillHoleJobStatus, setDrillHoleJobStatus] = useState(null);
    const [drillHoleJobId, setDrillHoleJobId] = useState(null);
    
    // Real-time progress tracking
    const [activeProgressJob, setActiveProgressJob] = useState(null); // 'soil' or 'drillhole'
    const [currentProgressJobId, setCurrentProgressJobId] = useState(null);
    
    // Soil corrections state
    const [selectedANumber, setSelectedANumber] = useState(null);
    const [correctionsModalShow, setCorrectionsModalShow] = useState(false);
    const [correctionsRefreshTrigger, setCorrectionsRefreshTrigger] = useState(0);

    useEffect(() => {
        fetchData();
    }, [])

    async function fetchData() {
        const result = await GoldFinderApi.get("/admin/reported_samples/soils");
        setReportedSoils(result.data);

        const drillHoleResult = await GoldFinderApi.get("/admin/reported_samples/drillholes");
        setReportedDrillHoles(drillHoleResult.data);

        const modifierResult = await GoldFinderApi.get("/admin/modifiers/soils")
        setSoilModifiers(modifierResult.data.soilModifiers);

        const drillHoleModifierResult = await GoldFinderApi.get("/admin/modifiers/drillholes")
        setDrillHoleModifiers(drillHoleModifierResult.data.drillHoleModifiers);
    }

    // Progress completion handlers
    const handleJobComplete = (completionData) => {
        console.log('Job completed:', completionData);
        
        if (activeProgressJob === 'soil') {
            setApplyingModifiers(false);
            setJobStatus({ 
                status: 'completed', 
                recordsAffected: completionData.total_modifier_records 
            });
        } else if (activeProgressJob === 'drillhole') {
            setApplyingDrillHoleModifiers(false);
            setDrillHoleJobStatus({ 
                status: 'completed', 
                recordsAffected: completionData.total_modifier_records 
            });
        }
        
        // Clear progress after a delay
        setTimeout(() => {
            setActiveProgressJob(null);
            setCurrentProgressJobId(null);
        }, 5000);
    };

    const handleJobFailed = (errorData) => {
        console.error('Job failed:', errorData);
        
        if (activeProgressJob === 'soil') {
            setApplyingModifiers(false);
            setJobStatus({ 
                status: 'failed', 
                errorMessage: errorData.error || 'Job failed' 
            });
        } else if (activeProgressJob === 'drillhole') {
            setApplyingDrillHoleModifiers(false);
            setDrillHoleJobStatus({ 
                status: 'failed', 
                errorMessage: errorData.error || 'Job failed' 
            });
        }
        
        // Clear progress after a delay
        setTimeout(() => {
            setActiveProgressJob(null);
            setCurrentProgressJobId(null);
        }, 5000);
    };

    const refreshModifiers = async () => {
        const modifierResult = await GoldFinderApi.get("/admin/modifiers/soils")
        setSoilModifiers(modifierResult.data.soilModifiers);
    }

    const refreshDrillHoleModifiers = async () => {
        const drillHoleModifierResult = await GoldFinderApi.get("/admin/modifiers/drillholes")
        setDrillHoleModifiers(drillHoleModifierResult.data.drillHoleModifiers);
    }

    const pollJobStatus = async (currentJobId) => {
        try {
            const response = await GoldFinderApi.get(`/admin/modifiers/job-status/${currentJobId}`);
            const status = response.data;
            
            setJobStatus(status);
            
            if (status.status === 'running') {
                // Continue polling every 2 seconds
                setTimeout(() => pollJobStatus(currentJobId), 2000);
            } else {
                // Job completed or failed
                setApplyingModifiers(false);
                if (status.status === 'completed') {
                    alert(`Modifier application completed successfully! ${status.recordsAffected} records were updated.`);
                } else if (status.status === 'failed') {
                    alert(`Modifier application failed: ${status.errorMessage}`);
                }
            }
        } catch (error) {
            console.error('Error polling job status:', error);
            setApplyingModifiers(false);
            alert('Failed to check job status. The job may still be running.');
        }
    };

    const handleApplyAllModifiers = async () => {
        const confirmed = window.confirm(
            "Are you sure you want to apply all modifiers to the whole dataset? " +
            "This will update all soil samples in the au_only_ss_assay_ppb_nov_v3 table. " +
            "This operation may take several minutes to complete."
        );
        
        if (confirmed) {
            setApplyingModifiers(true);
            setJobStatus(null);
            setActiveProgressJob(null); // Clear any existing progress
            
            try {
                const response = await GoldFinderApi.post("/admin/modifiers/apply-all");
                if (response.data.done && response.data.jobId) {
                    const newJobId = response.data.jobId;
                    setJobId(newJobId);
                    setCurrentProgressJobId(newJobId);
                    setActiveProgressJob('soil');
                    
                    // Start polling for status as backup
                    setTimeout(() => pollJobStatus(newJobId), 1000);
                }
            } catch (error) {
                console.error('Error starting modifier application:', error);
                alert('Failed to start modifier application. Please try again.');
                setApplyingModifiers(false);
            }
        }
    }

    const pollDrillHoleJobStatus = async (currentJobId) => {
        try {
            const response = await GoldFinderApi.get(`/admin/modifiers/job-status/${currentJobId}`);
            const status = response.data;
            
            setDrillHoleJobStatus(status);
            
            if (status.status === 'running') {
                // Continue polling every 2 seconds
                setTimeout(() => pollDrillHoleJobStatus(currentJobId), 2000);
            } else {
                // Job completed or failed
                setApplyingDrillHoleModifiers(false);
                if (status.status === 'completed') {
                    alert(`Drill hole modifier application completed successfully! ${status.recordsAffected} records were updated.`);
                } else if (status.status === 'failed') {
                    alert(`Drill hole modifier application failed: ${status.errorMessage}`);
                }
            }
        } catch (error) {
            console.error('Error polling drill hole job status:', error);
            setApplyingDrillHoleModifiers(false);
            alert('Failed to check drill hole job status. The job may still be running.');
        }
    };

    const handleApplyAllDrillHoleModifiers = async () => {
        const confirmed = window.confirm(
            "Are you sure you want to apply all drill hole modifiers to the whole dataset? " +
            "This will update all drill hole samples in the au_only_dh_nov table. " +
            "This operation may take several minutes to complete."
        );
        
        if (confirmed) {
            setApplyingDrillHoleModifiers(true);
            setDrillHoleJobStatus(null);
            setActiveProgressJob(null); // Clear any existing progress
            
            try {
                const response = await GoldFinderApi.post("/admin/modifiers/drillholes/apply-all");
                if (response.data.done && response.data.jobId) {
                    const newJobId = response.data.jobId;
                    setDrillHoleJobId(newJobId);
                    setCurrentProgressJobId(newJobId);
                    setActiveProgressJob('drillhole');
                    
                    // Start polling for status as backup
                    setTimeout(() => pollDrillHoleJobStatus(newJobId), 1000);
                }
            } catch (error) {
                console.error('Error starting drill hole modifier application:', error);
                alert('Failed to start drill hole modifier application. Please try again.');
                setApplyingDrillHoleModifiers(false);
            }
        }
    }

    // Soil corrections handlers
    const handleANumberClick = (anumber) => {
        setSelectedANumber(anumber);
        setCorrectionsModalShow(true);
    };

    const handleImportComplete = () => {
        setCorrectionsRefreshTrigger(prev => prev + 1);
    };

    const handleCorrectionDeleted = () => {
        setCorrectionsRefreshTrigger(prev => prev + 1);
    };

    return (
        <div className="container-fluid">
            <AddMultiplerModal 
                show={modalShow} 
                onHide={() => setModalShow(false)} 
                onSuccess={refreshModifiers}
            />
            <AddDrillHoleModifierModal 
                show={drillHoleModalShow} 
                onHide={() => setDrillHoleModalShow(false)} 
                onSuccess={refreshDrillHoleModifiers}
            />
            <ANumberCorrectionsModal 
                show={correctionsModalShow}
                onHide={() => setCorrectionsModalShow(false)}
                anumber={selectedANumber}
                onCorrectionDeleted={handleCorrectionDeleted}
            />
            
            <Row className="mb-4">
                <Col>
                    <h1>Admin Dashboard</h1>
                </Col>
            </Row>

            <Tab.Container defaultActiveKey="assay-modifiers">
                <Row>
                    <Col md={1}>
                        <Nav variant="pills" className="flex-column">
                            <Nav.Item>
                                <Nav.Link eventKey="assay-modifiers" className="mb-2">
                                    🧪 Assay Modifiers
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="drill-hole-modifiers" className="mb-2">
                                    🔧 Drill Hole Modifiers
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Col>
                    <Col md={11}>
                        <Tab.Content>
                            <Tab.Pane eventKey="assay-modifiers">
                                <Row>
                                    <Col md={6}>
                                        <h3>Reported Samples</h3>
                                        <Accordion>
                                            <Accordion.Item eventKey="0">
                                                <Accordion.Header>Reported Soil Samples</Accordion.Header>
                                                <Accordion.Body>
                                                    <ReportedSoilTable reportedSoils={reportedSoils.reportedSamples} refreshData={fetchData} />
                                                </Accordion.Body>
                                            </Accordion.Item>
                                            <Accordion.Item eventKey="1">
                                                <Accordion.Header>Reported Soils by A Number</Accordion.Header>
                                                <Accordion.Body>
                                                    <ReportedSoilANumberTable reportedSoils={reportedSoils.reportedAnumbers} refreshData={fetchData} />
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </Col>
                                    <Col md={6}>
                                        <h3>Assay Modifiers</h3>
                                        
                                        <Accordion className="mb-4">
                                            <Accordion.Item eventKey="0">
                                                <Accordion.Header>Soil Sample Modifiers</Accordion.Header>
                                                <Accordion.Body>
                                                    <div className="mb-3">
                                                        <Button 
                                                            onClick={() => { setModalShow(true); }}
                                                            className="me-2"
                                                        >
                                                            Add Soil Modifier
                                                        </Button>
                                                        <Button 
                                                            variant="warning" 
                                                            onClick={handleApplyAllModifiers}
                                                            disabled={applyingModifiers}
                                                        >
                                                            {applyingModifiers ? 'Applying...' : 'Apply Modifiers to Whole Dataset'}
                                                        </Button>
                                                        
                                                        {jobStatus && !activeProgressJob && (
                                                            <div className="mt-2">
                                                                <small className="text-muted">
                                                                    Job Status: <strong>{jobStatus.status}</strong>
                                                                    {jobStatus.status === 'running' && (
                                                                        <span> - Processing...</span>
                                                                    )}
                                                                    {jobStatus.status === 'completed' && jobStatus.recordsAffected && (
                                                                        <span> - {jobStatus.recordsAffected} records updated</span>
                                                                    )}
                                                                    {jobStatus.status === 'failed' && jobStatus.errorMessage && (
                                                                        <span className="text-danger"> - {jobStatus.errorMessage}</span>
                                                                    )}
                                                                </small>
                                                            </div>
                                                        )}
                                                        
                                                        {activeProgressJob === 'soil' && (
                                                            <ModifierProgressDisplay
                                                                jobId={currentProgressJobId}
                                                                onJobComplete={handleJobComplete}
                                                                onJobFailed={handleJobFailed}
                                                                apiBaseUrl={process.env.REACT_APP_GOLD_FINDER_API_URL || 'http://localhost:3000/service/api'}
                                                            />
                                                        )}
                                                    </div>
                                                    <SoilModifierTable soilModifier={soilModifiers} refreshData={refreshModifiers} />
                                                </Accordion.Body>
                                            </Accordion.Item>
                                            
                                            <Accordion.Item eventKey="1">
                                                <Accordion.Header>CSV Corrections</Accordion.Header>
                                                <Accordion.Body>
                                                    <div className="mb-4">
                                                        <h6>Import Corrections from CSV</h6>
                                                        <SoilCorrectionImport onImportComplete={handleImportComplete} />
                                                    </div>
                                                    
                                                    <div className="mt-4">
                                                        <h6>Corrected ANumbers</h6>
                                                        <CorrectedANumbersTable 
                                                            onANumberClick={handleANumberClick}
                                                            refreshTrigger={correctionsRefreshTrigger}
                                                        />
                                                    </div>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </Col>
                                </Row>
                            </Tab.Pane>
                            
                            <Tab.Pane eventKey="drill-hole-modifiers">
                                <Row>
                                    <Col md={6}>
                                        <h3>Reported Drill Hole Samples</h3>
                                        <Accordion>
                                            <Accordion.Item eventKey="0">
                                                <Accordion.Header>Reported Drill Hole Samples</Accordion.Header>
                                                <Accordion.Body>
                                                    <ReportedDrillHoleTable reportedDrillHoles={reportedDrillHoles.reportedSamples} refreshData={fetchData} />
                                                </Accordion.Body>
                                            </Accordion.Item>
                                            <Accordion.Item eventKey="1">
                                                <Accordion.Header>Reported Drill Holes by A Number</Accordion.Header>
                                                <Accordion.Body>
                                                    <ReportedDrillHoleANumberTable reportedDrillHoles={reportedDrillHoles.reportedAnumbers} refreshData={fetchData} />
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </Col>
                                    <Col md={6}>
                                        <h3>Drill Hole Modifiers</h3>
                                        <div className="mb-3">
                                            <Button 
                                                onClick={() => { setDrillHoleModalShow(true); }}
                                                className="me-2"
                                            >
                                                Add Drill Hole Modifier
                                            </Button>
                                            <Button 
                                                variant="warning" 
                                                onClick={handleApplyAllDrillHoleModifiers}
                                                disabled={true}
                                                className="me-2"
                                            >
                                                {applyingDrillHoleModifiers ? 'Applying...' : 'Apply All Drill Hole Modifiers'}
                                            </Button>
                                            
                                            {drillHoleJobStatus && !activeProgressJob && (
                                                <div className="mt-2">
                                                    <small className="text-muted">
                                                        Job Status: <strong>{drillHoleJobStatus.status}</strong>
                                                        {drillHoleJobStatus.status === 'running' && (
                                                            <span> - Processing...</span>
                                                        )}
                                                        {drillHoleJobStatus.status === 'completed' && drillHoleJobStatus.recordsAffected && (
                                                            <span> - {drillHoleJobStatus.recordsAffected} records updated</span>
                                                        )}
                                                        {drillHoleJobStatus.status === 'failed' && drillHoleJobStatus.errorMessage && (
                                                            <span className="text-danger"> - {drillHoleJobStatus.errorMessage}</span>
                                                        )}
                                                    </small>
                                                </div>
                                            )}
                                            
                                            {activeProgressJob === 'drillhole' && (
                                                <ModifierProgressDisplay
                                                    jobId={currentProgressJobId}
                                                    onJobComplete={handleJobComplete}
                                                    onJobFailed={handleJobFailed}
                                                    apiBaseUrl={process.env.REACT_APP_GOLD_FINDER_API_URL || 'http://localhost:3000/service/api'}
                                                />
                                            )}
                                        </div>
                                        <DrillHoleModifierTable drillHoleModifier={drillHoleModifiers} refreshData={refreshDrillHoleModifiers} />
                                    </Col>
                                </Row>
                            </Tab.Pane>
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container>
        </div>
    );
}

export default AdminPage;